import { Text, SimpleGrid, Box } from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import RadioInput from 'components/fields/home/RadioInput';
import SelectInput from 'components/fields/home/SelectInput';

import getRadioYesNo from 'helpers/getRadioYesNo';

import { changeDwellingRatingInformation } from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';

interface PlumbingSystemProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const PlumbingSystem = ({ dwelling, index }: PlumbingSystemProps) => {
  const dispatch = useAppDispatch();

  const onPlumbingTypeChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'plumbing_type',
        value: {
          ...dwelling.rating_information.plumbing_type,
          [field]: value,
        },
      }),
    );
  };

  const onWaterHeaterChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'water_heater_type',
        value: {
          ...dwelling.rating_information.water_heater_type,
          [field]: value,
        },
      }),
    );
  };

  const onWaterHeaterRadioChange = (value: string, field: string) => {
    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'water_heater_type',
        value: {
          ...dwelling.rating_information.water_heater_type,
          [field]: value === 'true',
        },
      }),
    );
  };

  const ratingInfo = dwelling.rating_information;

  if (!ratingInfo) return null;

  return (
    <Box mb="20px">
      {/* Plumbing Type */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Plumbing Type (Percentages)
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Copper %"
          name="plumbing_copper_percent"
          value={ratingInfo.plumbing_type.copper_percent || ''}
          onChange={(e: any) => onPlumbingTypeChange(e, 'copper_percent')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Galvanized %"
          name="plumbing_galvanized_percent"
          value={ratingInfo.plumbing_type.galvanized_percent || ''}
          onChange={(e: any) => onPlumbingTypeChange(e, 'galvanized_percent')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="ABS %"
          name="plumbing_abs_percent"
          value={ratingInfo.plumbing_type.abs_percent || ''}
          onChange={(e: any) => onPlumbingTypeChange(e, 'abs_percent')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="PVC %"
          name="plumbing_pvc_percent"
          value={ratingInfo.plumbing_type.pvc_percent || ''}
          onChange={(e: any) => onPlumbingTypeChange(e, 'pvc_percent')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="PEX %"
          name="plumbing_pex_percent"
          value={ratingInfo.plumbing_type.pex_percent || ''}
          onChange={(e: any) => onPlumbingTypeChange(e, 'pex_percent')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Poly B %"
          name="plumbing_poly_b_percent"
          value={ratingInfo.plumbing_type.poly_b_percent || ''}
          onChange={(e: any) => onPlumbingTypeChange(e, 'poly_b_percent')}
          disabled
        />
      </SimpleGrid>

      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Lead %"
          name="plumbing_lead_percent"
          value={ratingInfo.plumbing_type.lead_percent || ''}
          onChange={(e: any) => onPlumbingTypeChange(e, 'lead_percent')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Other %"
          name="plumbing_other_percent"
          value={ratingInfo.plumbing_type.other_percent || ''}
          onChange={(e: any) => onPlumbingTypeChange(e, 'other_percent')}
          disabled
        />
      </SimpleGrid>

      {/* Water Heater */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Water Heater
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <SelectInput
          section={SECTIONS.DWELLINGS}
          label="Water Heater Type"
          name="apparatus"
          value={ratingInfo.water_heater_type.apparatus || ''}
          onChange={(e: any) => onWaterHeaterChange(e, 'apparatus')}
          options={[
            { value: '', label: 'Select Type' },
            { value: 'Tank', label: 'Tank' },
            { value: 'Tankless', label: 'Tankless' },
          ]}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Water Heater Year"
          name="water_heater_year"
          value={ratingInfo.water_heater_type.water_heater_year || ''}
          onChange={(e: any) => onWaterHeaterChange(e, 'water_heater_year')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Fuel"
          name="water_heater_fuel"
          value={ratingInfo.water_heater_type.fuel || ''}
          onChange={(e: any) => onWaterHeaterChange(e, 'fuel')}
        />
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Professionally Installed"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            ratingInfo.water_heater_type.is_professionally_installed,
          )}
          onChange={(value: string) =>
            onWaterHeaterRadioChange(value, 'is_professionally_installed')
          }
          stacked
        />
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Approved ULC/CSA/WH"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            ratingInfo.water_heater_type.is_approved_ulc_csa_wh,
          )}
          onChange={(value: string) =>
            onWaterHeaterRadioChange(value, 'is_approved_ulc_csa_wh')
          }
          stacked
        />
      </SimpleGrid>

      {/* Plumbing Upgrades */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Plumbing Upgrades
      </Text>
      <SimpleGrid columns={6} spacing="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Full Year Upgrade"
          name="upgrades_plumbing_full_year"
          value={ratingInfo.upgrades.plumbing.full_year || ''}
          onChange={(event: any) => {
            const { value } = event.target;
            dispatch(
              changeDwellingRatingInformation({
                index,
                name: 'upgrades',
                value: {
                  ...ratingInfo.upgrades,
                  plumbing: {
                    ...ratingInfo.upgrades.plumbing,
                    full_year: value,
                  },
                },
              }),
            );
          }}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Partial Year Upgrade"
          name="upgrades_plumbing_partial_year"
          value={ratingInfo.upgrades.plumbing.partial_year || ''}
          onChange={(event: any) => {
            const { value } = event.target;
            dispatch(
              changeDwellingRatingInformation({
                index,
                name: 'upgrades',
                value: {
                  ...ratingInfo.upgrades,
                  plumbing: {
                    ...ratingInfo.upgrades.plumbing,
                    partial_year: value,
                  },
                },
              }),
            );
          }}
          disabled
        />
      </SimpleGrid>
    </Box>
  );
};

export default PlumbingSystem;
